import { Search } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AccountManagerSelect } from 'components/Common/AccountManagerSelect/AccountManagerSelect'
import AddressSearch from 'components/Common/AddressSearch/AddressSearch'
import { CountrySelect } from 'components/Common/CountrySelect/CountrySelect'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { getCompanyById } from 'reducers/companiesHouseReducer'

export function CompanyDetails() {
  const {
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext()

  const dispatch = useDispatch()
  const companyLocation = watch('address.countryCode')
  const hasMounted = useRef(false)

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    void trigger('address.postCode')
  }, [companyLocation, trigger])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '80%',
      }}
    >
      <Typography>Company Information</Typography>
      <Box sx={{ display: 'flex', gap: '10px' }}>
        <Controller
          name="registrationNumber"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                sx={{ flexGrow: 1 }}
                {...field}
                label={'Registration Number'}
                variant="outlined"
                required={
                  (!!errors.registrationNumber &&
                    !!(errors.registrationNumber as any)) ||
                  (!!errors.validated && !!(errors.validated as any))
                }
                error={
                  (!!errors.registrationNumber &&
                    !!(errors.registrationNumber as any)) ||
                  (!!errors.validated && !!(errors.validated as any))
                }
                helperText={
                  (errors.registrationNumber &&
                    (errors.registrationNumber as any)?.message) ||
                  (errors.validated && (errors.validated as any)?.message)
                }
                InputProps={{
                  endAdornment:
                    companyLocation === 'GBR' ? (
                      <InputAdornment position="end">
                        <Tooltip title="Fetch info from UK Companies House">
                          <IconButton
                            onClick={async () => {
                              console.log(getValues('registrationNumber'))
                              try {
                                const action = await dispatch(
                                  getCompanyById(
                                    getValues('registrationNumber'),
                                  ),
                                )
                                const response = (action as any).payload

                                setValue('companyName', response.companyName)
                                setValue(
                                  'address.addressLine1',
                                  response.addressLine1,
                                )
                                setValue(
                                  'address.addressLine2',
                                  response.addressLine2,
                                )
                                setValue('address.town', response.town)
                                setValue('address.county', response.county)
                                setValue('address.postCode', response.postCode)
                                setValue('address.postCode', response.postCode)
                                setValue('validated', true)
                                clearErrors('address')
                              } catch (err) {
                                console.log('unable to fetch', err)
                              }
                            }}
                          >
                            <Search />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                }}
              />
              {errors.root?.message && (
                <Typography color="error" variant="body2">
                  {errors.root.message}
                </Typography>
              )}
            </>
          )}
        />
        <Controller
          name="validated"
          control={control}
          render={({ field }) => (
            <>
              <Checkbox
                {...field}
                checked={field.value}
                sx={{ display: 'none' }}
              />
            </>
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', width: '100%', gap: '10px' }}>
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ flexGrow: 1 }}
              label={'Company Name'}
              required={!!errors.companyName && !!(errors.companyName as any)}
              error={!!errors.companyName && !!(errors.companyName as any)}
              helperText={
                errors.companyName && (errors.companyName as any)?.message
              }
            />
          )}
        />
        <Controller
          name="alias"
          control={control}
          render={({ field }) => (
            <>
              <TextField {...field} sx={{ flexGrow: 1 }} label={'Trading As'} />
            </>
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        <Typography>Company Address</Typography>

        {companyLocation !== 'GBR' && (
          <AddressSearch
            clientId={1}
            styles={{
              'min-height': '52px !important',
              'z-index': '99',
            }}
            onAddresssSelected={(e) => {
              setValue('address.addressLine1', e.addressLine1)
              setValue('address.addressLine2', e.addressLine2)
              setValue('address.town', e.town)
              setValue('address.county', e.county)
              setValue('address.postCode', e.postCode)
              setValue('address.countryCode', e.countryCode)
              console.log('ee', e)
            }}
            addressPlaceholder={'Search for an address'}
            name={'address'}
          />
        )}

        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Controller
            name="address.addressLine1"
            control={control}
            render={({ field }) => (
              <TextField
                sx={{ flexGrow: 0.3 }}
                {...field}
                label={'Street Address'}
                required={
                  !!errors.address && !!(errors.address as any).addressLine1
                }
                error={
                  !!errors.address && !!(errors.address as any).addressLine1
                }
                helperText={
                  !!errors.address &&
                  !!(errors.address as any).addressLine1?.message
                }
              />
            )}
          />
          <Controller
            name="address.addressLine2"
            control={control}
            render={({ field }) => (
              <TextField
                sx={{ flexGrow: 1 }}
                {...field}
                label={'Street Address Line 2'}
              />
            )}
          />
        </Box>

        <Controller
          name="address.town"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ flexGrow: 1 }}
              label={'Town / City'}
              required={
                !!errors.address?.town && !!(errors.address?.town as any)
              }
              error={!!errors.address?.town && !!(errors.address?.town as any)}
              helperText={
                errors.address?.town && (errors.address?.town as any)?.message
              }
            />
          )}
        />
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Controller
            name="address.county"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ flexGrow: 1 }}
                label={'County / Province'}
                required={!!errors.address && !!(errors.address as any).county}
                error={!!errors.address && !!(errors.address as any).county}
                helperText={
                  errors.address && (errors.address as any).county?.message
                }
              />
            )}
          />
          <Controller
            name="address.postCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ flexGrow: 0.3 }}
                label={
                  companyLocation === 'GBR'
                    ? 'Post Code'
                    : companyLocation === 'USA'
                    ? 'ZIP code'
                    : 'Postal / Zip code'
                }
                required={
                  !!errors.address && !!(errors.address as any).postCode
                }
                error={!!errors.address && !!(errors.address as any).postCode}
                helperText={
                  errors.address && (errors.address as any).postCode?.message
                }
              />
            )}
          />
        </Box>
        <Box>
          <Controller
            name="address.countryCode"
            control={control}
            render={({ field }) => (
              <CountrySelect {...field} sibling="address.country" />
            )}
          />
        </Box>
      </Box>
      <Typography>Company Contact</Typography>
      <Box sx={{ display: 'flex', gap: '10px' }}>
        <Controller
          name="contact.name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ flexGrow: 0.5 }}
              label={'Contact Name'}
              required={!!errors.contact && !!(errors.contact as any).name}
              error={!!errors.contact && !!(errors.contact as any).name}
              helperText={
                errors.contact && (errors.contact as any).contact?.name
              }
            />
          )}
        />
        <Controller
          name="contact.email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ flexGrow: 0.5 }}
              label={'Email Address'}
              required={!!errors.contact && !!(errors.contact as any).email}
              error={!!errors.contact && !!(errors.contact as any).email}
              helperText={
                errors.contact && (errors.contact as any).email?.message
              }
            />
          )}
        />
        <Controller
          name="contact.contactNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ flexGrow: 0.5 }}
              label={'Phone Number'}
              required={
                !!errors.contact && !!(errors.contact as any).contactNumber
              }
              error={
                !!errors.contact && !!(errors.contact as any).contactNumber
              }
              helperText={
                errors.contact && (errors.contact as any).contactNumber?.message
              }
            />
          )}
        />
      </Box>

      <Typography>Account Manager</Typography>

      <Box>
        <Controller
          name="accountManager"
          control={control}
          render={({ field }) => {
            return (
              <AccountManagerSelect
                {...field}
                required={
                  !!errors.accountManager && !!(errors.accountManager as any)
                }
                error={
                  !!errors.accountManager && !!(errors.accountManager as any)
                }
                helperText={
                  errors.accountManager &&
                  (errors.accountManager as any)?.message
                }
              />
            )
          }}
        />
      </Box>
    </Box>
  )
}
